import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Clock, CheckCircle, XCircle, ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const PracticePage = () => {
  const { subject } = useParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState(Date.now());
  const [sessionStats, setSessionStats] = useState({ correct: 0, total: 0 });
  const [showReflection, setShowReflection] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, [subject]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await apiService.getQuestions(subject, 5);
      setQuestions(response.data.questions);
      setStartTime(Date.now());
    } catch (error) {
      toast.error('Failed to load questions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!selectedAnswer) {
      toast.error('Please select an answer');
      return;
    }

    const responseTime = (Date.now() - startTime) / 1000;
    const currentQuestion = questions[currentIndex];

    try {
      const response = await apiService.submitAnswer({
        question_id: currentQuestion.id,
        submitted_answer: selectedAnswer,
        response_time_seconds: responseTime,
        difficulty: currentQuestion.difficulty
      });

      setResult(response.data);
      setShowResult(true);
      setSessionStats(prev => ({
        correct: prev.correct + (response.data.is_correct ? 1 : 0),
        total: prev.total + 1
      }));

      // Update user context
      if (response.data.new_badges?.length > 0) {
        toast.success(`New badge(s) unlocked: ${response.data.new_badges.join(', ')}!`);
      }
    } catch (error) {
      toast.error('Failed to submit answer');
      console.error(error);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer('');
      setShowResult(false);
      setResult(null);
      setStartTime(Date.now());
    } else {
      setShowReflection(true);
    }
  };

  const handleFinishSession = () => {
    navigate('/dashboard');
    toast.success('Great practice session!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#4D96FF] animate-spin mx-auto mb-4" />
          <p className="text-lg font-semibold text-[#64748B]">Loading questions...</p>
        </div>
      </div>
    );
  }

  if (showReflection) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl" data-testid="reflection-modal">
          <h2 className="text-4xl font-black text-[#1E293B] mb-6 text-center">Session Complete! 🎉</h2>
          
          <div className="bg-[#D8E8FF] rounded-2xl p-6 mb-6">
            <div className="text-center">
              <p className="text-6xl font-black text-[#4D96FF] mb-2">
                {sessionStats.correct}/{sessionStats.total}
              </p>
              <p className="text-xl font-bold text-[#64748B]">Questions Correct</p>
              <p className="text-lg text-[#64748B] mt-2">
                {((sessionStats.correct / sessionStats.total) * 100).toFixed(0)}% Accuracy
              </p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-semibold text-[#1E293B] mb-2">
                What did you learn today? 💡
              </label>
              <textarea
                data-testid="reflection-learned-input"
                className="w-full p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-[#4D96FF] focus:ring-4 focus:ring-[#4D96FF]/10"
                rows={3}
                placeholder="I learned that..."
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1E293B] mb-2">
                How difficult was this session?
              </label>
              <div className="flex gap-3 justify-center">
                {['😊', '😐', '😰'].map((emoji, idx) => (
                  <button
                    key={idx}
                    data-testid={`difficulty-rating-${idx + 1}`}
                    className="w-16 h-16 text-4xl bg-[#F4F8FB] rounded-2xl hover:bg-[#D8E8FF] transition-all border-2 border-slate-200 hover:border-[#4D96FF]"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            data-testid="finish-session-btn"
            onClick={handleFinishSession}
            className="w-full py-4 bg-[#4D96FF] hover:bg-[#3B7BE0] text-white rounded-2xl font-bold text-lg border-b-4 border-[#3B7BE0] active:border-b-0 active:translate-y-1 transition-all"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="practice-page">
      {/* Header */}
      <div className="bg-white border-b-2 border-slate-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-[#64748B] hover:text-[#1E293B] font-semibold"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-sm text-[#64748B] font-semibold">Question</p>
                <p className="text-xl font-black text-[#1E293B]">{currentIndex + 1}/{questions.length}</p>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-[#4D96FF] to-[#A493FF] h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="inline-block px-4 py-2 bg-[#D8E8FF] text-[#4D96FF] rounded-xl font-bold text-sm mb-4 capitalize">
                {subject}
              </div>
              <h2 data-testid="question-text" className="text-2xl font-bold text-[#1E293B] leading-relaxed">
                {currentQuestion?.text}
              </h2>
            </div>
          </div>

          {/* Answer Options */}
          <div className="space-y-4">
            {currentQuestion?.answers.map((answer, idx) => {
              const isSelected = selectedAnswer === answer;
              const showCorrect = showResult && answer === result?.correct_answer;
              const showWrong = showResult && isSelected && !result?.is_correct;

              return (
                <button
                  key={idx}
                  data-testid={`answer-option-${idx}`}
                  onClick={() => !showResult && setSelectedAnswer(answer)}
                  disabled={showResult}
                  className={`w-full p-5 rounded-2xl border-2 text-left font-semibold transition-all ${
                    showCorrect
                      ? 'bg-green-100 border-green-500 text-green-900'
                      : showWrong
                      ? 'bg-red-100 border-red-500 text-red-900'
                      : isSelected
                      ? 'bg-[#D8E8FF] border-[#4D96FF] text-[#1E293B]'
                      : 'bg-[#F4F8FB] border-slate-200 text-[#1E293B] hover:border-[#4D96FF]'
                  } ${!showResult && 'hover:scale-[1.02]'}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{answer}</span>
                    {showCorrect && <CheckCircle className="w-6 h-6 text-green-600" />}
                    {showWrong && <XCircle className="w-6 h-6 text-red-600" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Result */}
          {showResult && (
            <div data-testid="answer-result" className={`mt-6 p-6 rounded-2xl ${
              result?.is_correct 
                ? 'bg-green-100 border-2 border-green-500'
                : 'bg-red-100 border-2 border-red-500'
            }`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                  result?.is_correct ? 'bg-green-500' : 'bg-red-500'
                }`}>
                  {result?.is_correct ? (
                    <CheckCircle className="w-7 h-7 text-white" />
                  ) : (
                    <XCircle className="w-7 h-7 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#1E293B] mb-2">
                    {result?.is_correct ? 'Correct! 🎉' : 'Not quite right'}
                  </h3>
                  <p className="text-[#64748B] font-medium mb-1 italic">
                    {result?.feedback || (result?.is_correct ? 'Great job!' : 'Keep trying!')}
                  </p>
                  {!result?.is_correct && (
                    <p className="text-[#64748B] font-medium mb-2">
                      The correct answer is: <strong>{result?.correct_answer}</strong>
                    </p>
                  )}
                  <div className="flex gap-4 mt-3">
                    <div className="text-sm">
                      <span className="text-[#64748B]">XP Gained:</span>
                      <span className="font-bold text-[#1E293B] ml-1">+{result?.xp_gained}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-[#64748B]">Elo Change:</span>
                      <span className={`font-bold ml-1 ${
                        result?.elo_change > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {result?.elo_change > 0 ? '+' : ''}{result?.elo_change}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Button */}
          <div className="mt-6">
            {!showResult ? (
              <button
                data-testid="submit-answer-btn"
                onClick={handleSubmitAnswer}
                className="w-full py-4 bg-[#4D96FF] hover:bg-[#3B7BE0] text-white rounded-2xl font-bold text-lg border-b-4 border-[#3B7BE0] active:border-b-0 active:translate-y-1 transition-all"
              >
                Submit Answer
              </button>
            ) : (
              <button
                data-testid="next-question-btn"
                onClick={handleNext}
                className="w-full py-4 bg-[#FFC03D] hover:bg-[#E6A820] text-white rounded-2xl font-bold text-lg border-b-4 border-[#E6A820] active:border-b-0 active:translate-y-1 transition-all"
              >
                {currentIndex < questions.length - 1 ? 'Next Question →' : 'Finish Session'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticePage;